import turfBearing from "@turf/bearing";
import turfBooleanPointInPolygon from "@turf/boolean-point-in-polygon";
import turfCenterOfMass from "@turf/center-of-mass";
import turfDestination from "@turf/destination";
import { Feature, FeatureCollection, Point, Polygon, Properties, lineString as turfLineString } from "@turf/helpers";
import turfLineIntersect from "@turf/line-intersect";
import turfMidpoint from "@turf/midpoint";
import turfNearestPointOnLine from "@turf/nearest-point-on-line";
import turfPolygonToLine from "@turf/polygon-to-line";
import type { LatLngExpression } from "leaflet";
import { WGS84_POLE_OF_INACCESSIBILITY_PRECISION, calculatePoleOfInaccessibility } from "./calculate-pole-of-inaccessibility";

const OPPOSITE_BEARING_ANGLE = 180;

export function calculateBalancedCenter(geometry: Feature<Polygon, Properties>): LatLngExpression {
    let intersections: FeatureCollection<Point, Properties> | undefined;

    const centerOfMass = turfCenterOfMass(geometry);
    const edgeLines = turfPolygonToLine(geometry);

    if (turfBooleanPointInPolygon(centerOfMass, geometry) && edgeLines.type === "Feature") {
        // NOTE: find nearest point on the edge of the polygon
        const nearestEdge = turfNearestPointOnLine(edgeLines.geometry, centerOfMass);

        // NOTE: calculate bearing between center and nearest edge
        const bearing = turfBearing(centerOfMass, nearestEdge);

        // NOTE: extend the line in both directions
        const line = turfLineString([
            turfDestination(centerOfMass, 100, bearing, { units: "kilometers" }).geometry.coordinates,
            turfDestination(centerOfMass, 100, bearing + OPPOSITE_BEARING_ANGLE, { units: "kilometers" }).geometry.coordinates,
        ]);

        // NOTE: find intersection of the line with the polygon
        intersections = turfLineIntersect(line, edgeLines);
    }

    if (intersections?.features.length !== 2) {
        const poleOfInaccessibility = calculatePoleOfInaccessibility(
            geometry.geometry.coordinates,
            WGS84_POLE_OF_INACCESSIBILITY_PRECISION
        );

        return [poleOfInaccessibility[1], poleOfInaccessibility[0]];
    }

    const midpoint = turfMidpoint(intersections.features[0], intersections.features[1]);

    return [midpoint.geometry.coordinates[1], midpoint.geometry.coordinates[0]];
}
